import React from 'react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { BreadTransferRow, UpdateTransferRowPayload } from '../types/bread-transfers.types';

interface AfternoonOrderTableProps {
  locations: BreadTransferRow[];
  totals: {
    historicAvgSaleTotal: number;
    totalSalePreviousDay: number;
    totalSalePreviousWeek: number;
    quantityToRequest: number;
    safetyFactor: number;
    requestToBakery: number;
  };
  onUpdateRow: (payload: UpdateTransferRowPayload) => void;
  onTotalChange?: (total: number) => void;
  disabled?: boolean;
}

export const AfternoonOrderTable: React.FC<AfternoonOrderTableProps> = ({
  locations,
  totals,
  onUpdateRow,
  onTotalChange,
  disabled = false
}) => {
  const [safetyFactor, setSafetyFactor] = React.useState(0);
  const [orderValues, setOrderValues] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    if (onTotalChange) {
      const total = calculateTotalOrderToBakery();
      onTotalChange(total);
    }
  }, [safetyFactor, orderValues, onTotalChange]);

  const handleInputChange = (
    locationId: string,
    field: keyof Omit<BreadTransferRow, 'locationId' | 'locationCode' | 'locationName' | 'calculatedDispatch'>,
    value: string
  ) => {
    const numValue = parseInt(value) || 0;
    onUpdateRow({
      locationId,
      field,
      value: numValue
    });
  };

  const handleSafetyFactorChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    setSafetyFactor(numValue);
  };

  const handleOrderValueChange = (locationId: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setOrderValues(prev => ({
      ...prev,
      [locationId]: numValue
    }));
  };

  const getOrderValue = (locationId: string, defaultValue: number) => {
    return orderValues[locationId] !== undefined ? orderValues[locationId] : defaultValue;
  };

  const getOrderValueForTotal = (locationId: string, defaultValue: number) => {
    return orderValues[locationId] !== undefined ? orderValues[locationId] : defaultValue;
  };

  const calculateSafetyFactorForLocation = (quantityToRequest: number) => {
    const factor = safetyFactor / 100;
    return Math.round(quantityToRequest * factor);
  };

  const calculateOrderToBakery = (quantityToRequest: number) => {
    const safetyAmount = calculateSafetyFactorForLocation(quantityToRequest);
    return quantityToRequest + safetyAmount;
  };

  const calculateTotalOrderToBakery = () => {
    return locations.reduce((sum, location) => {
      const mockData = {
        'MAC 01 (ANGAMOS)': { quantity: 260 },
        'MAC 02 (LA ROTONDA)': { quantity: 175 },
        'MAC 03 (BENAVIDES)': { quantity: 95 },
        'MAC 04 (SURQUILLO)': { quantity: 53 },
        'MAC 05 (UPC)': { quantity: 44 },
        'MAC 07 (LA FONTANA)': { quantity: 136 },
        'MAC 08 (ULIMA)': { quantity: 39 },
        'MAC 09 (MIRAFLORES)': { quantity: 81 }
      };
      
      const key = `${location.locationCode} (${location.locationName})` as keyof typeof mockData;
      const data = mockData[key] || mockData['MAC 01 (ANGAMOS)'];
      const defaultValue = calculateOrderToBakery(data.quantity);
      const actualValue = getOrderValueForTotal(key, defaultValue);
      return sum + actualValue;
    }, 0);
  };

  const calculateTotalSafetyFactor = () => {
    const mockData = {
      'MAC 01 (ANGAMOS)': { quantity: 260 },
      'MAC 02 (LA ROTONDA)': { quantity: 175 },
      'MAC 03 (BENAVIDES)': { quantity: 95 },
      'MAC 04 (SURQUILLO)': { quantity: 53 },
      'MAC 05 (UPC)': { quantity: 44 },
      'MAC 07 (LA FONTANA)': { quantity: 136 },
      'MAC 08 (ULIMA)': { quantity: 39 },
      'MAC 09 (MIRAFLORES)': { quantity: 81 }
    };
    
    return Object.values(mockData).reduce((sum, data) => sum + calculateSafetyFactorForLocation(data.quantity), 0);
  };

  return (
    <Card>
      <CardContent>
        <div className="flex justify-end items-center mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="safety-factor" className="text-sm font-medium text-gray-700">
                Factor seguridad %:
              </label>
              <Input
                id="safety-factor"
                type="number"
                value={safetyFactor}
                onChange={(e) => handleSafetyFactorChange(e.target.value)}
                className="w-20 text-center font-semibold text-sm h-8"
                min="0"
                max="100"
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LOCAL</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HISTÓRICO PROMEDIO VENTA TOTAL
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VENTA TOTAL DÍA ANTERIOR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VENTA SEMANA ANTERIOR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CANTIDAD A SOLICITAR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-yellow-50">
                    FACTOR DE SEGURIDAD
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                    PEDIDO A PANADERÍA
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {locations.map((location) => {
                  // Datos simulados específicos para pedido tarde basados en la imagen
                  const mockData = {
                    'MAC 01 (ANGAMOS)': { historic: 430, totalPrevious: 276, weekPrevious: 371, quantity: 260, factor: 0, order: 150 },
                    'MAC 02 (LA ROTONDA)': { historic: 345, totalPrevious: 250, weekPrevious: 311, quantity: 175, factor: 0, order: 150 },
                    'MAC 03 (BENAVIDES)': { historic: 274, totalPrevious: 253, weekPrevious: 274, quantity: 95, factor: 0, order: 100 },
                    'MAC 04 (SURQUILLO)': { historic: 123, totalPrevious: 71, weekPrevious: 94, quantity: 53, factor: 0, order: 50 },
                    'MAC 05 (UPC)': { historic: 112, totalPrevious: 120, weekPrevious: 108, quantity: 44, factor: 0, order: 40 },
                    'MAC 07 (LA FONTANA)': { historic: 196, totalPrevious: 149, weekPrevious: 184, quantity: 136, factor: 0, order: 140 },
                    'MAC 08 (ULIMA)': { historic: 159, totalPrevious: 133, weekPrevious: 159, quantity: 39, factor: 0, order: 40 },
                    'MAC 09 (MIRAFLORES)': { historic: 121, totalPrevious: 70, weekPrevious: 121, quantity: 81, factor: 0, order: 80 }
                  };
                  
                  const key = `${location.locationCode} (${location.locationName})` as keyof typeof mockData;
                  const data = mockData[key] || mockData['MAC 01 (ANGAMOS)'];
                  
                  return (
                    <tr key={location.locationId} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-sm text-gray-900">
                          {location.locationCode} ({location.locationName})
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="font-semibold text-sm text-gray-900">
                          {data.historic}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="font-semibold text-sm text-gray-900">
                          {data.totalPrevious}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="font-semibold text-sm text-gray-900">
                          {data.weekPrevious}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="font-semibold text-sm text-gray-900">
                          {data.quantity}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center bg-yellow-50">
                        <span className="font-semibold text-sm text-gray-900">
                          {calculateSafetyFactorForLocation(data.quantity)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center bg-blue-50">
                        <div className="flex justify-center">
                          <Input
                            type="number"
                            value={getOrderValue(key, calculateOrderToBakery(data.quantity))}
                            onChange={(e) => handleOrderValueChange(key, e.target.value)}
                            className="w-20 text-center font-bold text-blue-700 text-sm h-8"
                            min="0"
                            disabled={disabled}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                
                {/* Totals Row */}
                <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                  <td className="px-3 py-2 text-gray-900">Totales</td>
                  <td className="px-3 py-2 text-center text-gray-900">1,760</td>
                  <td className="px-3 py-2 text-center text-gray-900">1,322</td>
                  <td className="px-3 py-2 text-center text-gray-900">1,622</td>
                  <td className="px-3 py-2 text-center text-gray-900">883</td>
                  <td className="px-3 py-2 text-center bg-yellow-100 text-gray-900">{calculateTotalSafetyFactor()}</td>
                  <td className="px-3 py-2 text-center bg-blue-100 text-blue-800 text-lg font-bold">
                    {calculateTotalOrderToBakery()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};